import { useCallback, useEffect, useMemo, useState } from "react";
import { teacherApi } from "@/services/api/teacher";
import type {
  ColleagueTeacher,
  TeacherTimetableEntry,
} from "@/services/api/teacher";
import { EntriesTab } from "@/components/timetable-import/EntriesTab";
import {
  clockRangeKey,
  templateAxisKey,
} from "@/components/timetable-import/timeUtils";
import { SubjectRebindPanel } from "@/components/timetable-import/SubjectRebindPanel";
import { TeacherLetterDirectory } from "@/components/teacher/TeacherLetterDirectory";
import { EffectivePlanView } from "@/components/effective-plan/EffectivePlanView";
import { TeacherCode } from "@/components/teacher/TeacherCode";
import { useTeacherData } from "@/hooks/useTeacherData";
import type {
  RebindCategory,
  SubjectRebindPreview,
} from "@/services/api/timetable";
import type { SubjectRebindSaveOpts } from "@/components/timetable-import/SubjectRebindPanel";
import { extractErrorMessage } from "@/utils/errorHandler";
import { useToast } from "@/components/ui/ToastProvider";
import {
  timetableExportApi,
  type ExportOptions,
} from "@/services/api/timetableExport";
import { TeacherPrintBar } from "@/components/timetable-export/TeacherPrintBar";
import { sameWeek, type WeekKey } from "@/components/effective-plan/planFormat";
import type { EffectivePlan } from "@/services/api/effectivePlan";

export function TeacherTimetablePage() {
  const {
    timetableEntries,
    setTimetableEntries,
    schoolTimeSlots,
    isLoadingTimetableEntries,
    error,
    setError,
    message,
    setMessage,
    fetchTimetableEntries,
  } = useTeacherData();
  const [rebind, setRebind] = useState<{
    preview: SubjectRebindPreview;
    anchor: DOMRect;
    source?: RebindCategory;
  } | null>(null);
  const [rebindSaving, setRebindSaving] = useState(false);
  const [rebindError, setRebindError] = useState<string | null>(null);
  const [colleagues, setColleagues] = useState<ColleagueTeacher[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showSaturday, setShowSaturday] = useState(false);
  const [peerEntries, setPeerEntries] = useState<TeacherTimetableEntry[]>([]);
  const [peerLoading, setPeerLoading] = useState(false);
  const { showToast } = useToast();
  const [exportOptions, setExportOptions] = useState<ExportOptions | null>(
    null
  );
  const [printOpen, setPrintOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<WeekKey | null>(null);
  const [effectivePlan, setEffectivePlan] = useState<EffectivePlan | null>(
    null
  );
  const [pdfGrid, setPdfGrid] = useState<{
    days: string[];
    rows: Array<{ label: string; lesson?: number | null }>;
  } | null>(null);

  useEffect(() => {
    timetableExportApi
      .options()
      .then(setExportOptions)
      .catch(() => setExportOptions(null));
    teacherApi
      .getTimetableGrid()
      .then(setPdfGrid)
      .catch(() => setPdfGrid(null));
  }, []);

  const isOwn = !selectedId || !selfId || selectedId === selfId;

  useEffect(() => {
    let live = true;
    void teacherApi
      .listColleagues()
      .then((res) => {
        if (!live) return;
        setColleagues(res.teachers);
        setSelfId(res.self_id);
        setSelectedId((prev) => prev ?? res.self_id);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Lehrpersonen konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [setError]);

  useEffect(() => {
    if (!selectedId || !selfId || selectedId === selfId) {
      setPeerEntries([]);
      setPeerLoading(false);
      return;
    }
    const requestedId = selectedId;
    let live = true;
    setPeerEntries([]);
    setPeerLoading(true);
    void teacherApi
      .getMyTimetableEntries(undefined, undefined, requestedId)
      .then((entries) => {
        if (!live) return;
        setPeerEntries(entries);
      })
      .catch((err) => {
        if (!live) return;
        setPeerEntries([]);
        setError(
          extractErrorMessage(err, {
            loadError: "Stundenplan konnte nicht geladen werden",
          })
        );
      })
      .finally(() => {
        if (live) setPeerLoading(false);
      });
    return () => {
      live = false;
    };
  }, [selectedId, selfId, setError]);

  const visibleColleagues = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return colleagues;
    return colleagues.filter(
      (p) =>
        p.last_name.toLowerCase().includes(t) ||
        p.first_name.toLowerCase().includes(t) ||
        `${p.last_name}, ${p.first_name}`.toLowerCase().includes(t) ||
        (p.teacher_code || "").toLowerCase().includes(t)
    );
  }, [colleagues, query]);

  const selected = colleagues.find((p) => p.id === selectedId) ?? null;
  const sourceEntries = isOwn ? timetableEntries : peerEntries;
  const pdfAxisKeys = (pdfGrid?.rows ?? []).map((row, index) =>
    templateAxisKey(row.label, row.lesson, index)
  );
  const displayEntries = sourceEntries.map((entry) => {
    const fromLabel = clockRangeKey(entry.time_slot_label);
    const named = schoolTimeSlots.find(
      (item) => item.name && item.name === entry.time_slot_label
    );
    const fromSlot =
      named?.start_time && named.end_time
        ? `${named.start_time.slice(0, 5).padStart(5, "0")}–${named.end_time.slice(0, 5).padStart(5, "0")}`
        : null;
    const clock = fromLabel ?? fromSlot;
    const byLesson =
      entry.lesson_number != null
        ? (pdfGrid?.rows ?? []).findIndex(
            (row) => row.lesson === entry.lesson_number
          )
        : -1;
    const byClock = clock
      ? (pdfGrid?.rows ?? []).findIndex((row) => row.label === clock)
      : -1;
    const hit = byLesson >= 0 ? byLesson : byClock;
    if (hit >= 0 && pdfAxisKeys[hit])
      return { ...entry, time_slot_label: pdfAxisKeys[hit] };
    if (clock) return { ...entry, time_slot_label: clock };
    return entry;
  });
  const gridRows = pdfAxisKeys;
  const shownEntries = useMemo(
    () =>
      effectiveEntries(
        displayEntries,
        effectivePlan,
        selectedWeek,
        pdfAxisKeys,
        pdfGrid?.rows ?? []
      ),
    [displayEntries, effectivePlan, selectedWeek, pdfAxisKeys, pdfGrid]
  );
  const displayLoading = isOwn ? isLoadingTimetableEntries : peerLoading;

  const handleSaveTimetableEntry = useCallback(
    async (entry: TeacherTimetableEntry): Promise<TeacherTimetableEntry> => {
      if (!isOwn) throw new Error("Stundenplan ist nur lesbar");
      if (!entry.id) {
        return await teacherApi.createTimetableEntry({
          day_of_week: entry.day_of_week ?? undefined,
          time_slot_label: entry.time_slot_label || "",
          subject: entry.subject ?? undefined,
          room: entry.room ?? undefined,
          class_name: entry.class_name ?? undefined,
          lesson_number: entry.lesson_number ?? undefined,
        });
      }
      try {
        return await teacherApi.updateTimetableEntry(
          entry.id,
          {
            subject: entry.subject ?? undefined,
            room: entry.room ?? undefined,
            class_name: entry.class_name ?? undefined,
            time_slot_label: entry.time_slot_label ?? undefined,
          },
          entry.version
        );
      } catch (e: any) {
        if (e?.statusCode === 409) {
          setError("Eintrag wurde zwischenzeitlich geändert. Bitte neu laden.");
          fetchTimetableEntries();
          throw e;
        }
        throw e;
      }
    },
    [fetchTimetableEntries, isOwn, setError]
  );

  const handleDeleteTimetableEntry = useCallback(
    async (entry: TeacherTimetableEntry): Promise<void> => {
      if (!isOwn) return;
      if (entry.id) {
        await teacherApi.deleteTimetableEntry(entry.id);
        setTimetableEntries((prev) =>
          prev.filter((e) => e.id && e.id !== entry.id)
        );
      }
    },
    [isOwn, setTimetableEntries]
  );

  const handleUpdateTimetableEntry = useCallback(
    (index: number, updatedEntry: TeacherTimetableEntry) => {
      setTimetableEntries((prev) => {
        const next = [...prev];
        next[index] = updatedEntry;
        return next;
      });
    },
    [setTimetableEntries]
  );

  const handleAddTimetableEntry = useCallback(
    (entry: TeacherTimetableEntry) => {
      setTimetableEntries((prev) => [...prev, entry]);
    },
    [setTimetableEntries]
  );

  const handleRebindSubject = useCallback(
    async (
      entry: TeacherTimetableEntry,
      anchor: DOMRect,
      field?: RebindCategory
    ) => {
      if (!isOwn || !entry.id) return;
      try {
        const preview = await teacherApi.previewSubjectRebind(entry.id);
        setRebindError(null);
        setRebind({ preview, anchor, source: field });
      } catch (err) {
        setError(
          extractErrorMessage(err, {
            loadError: "Fachliste konnte nicht geladen werden",
          })
        );
      }
    },
    [isOwn, setError]
  );

  const persistRebind = async (opts: SubjectRebindSaveOpts) => {
    if (!rebind || !isOwn) return;
    setRebindSaving(true);
    setRebindError(null);
    try {
      const result = await teacherApi.rebindSubject(rebind.preview.entry_id, {
        subject_id: opts.targetId,
        target_id: opts.targetId,
        source_category: opts.sourceCategory,
        target_category: opts.targetCategory,
        include_siblings: opts.includeSiblings,
      });
      const undo = result.undo;
      showToast({
        message: `${result.updated_ids.length} Lektion${result.updated_ids.length === 1 ? "" : "en"} → ${result.target_name || result.subject_name}`,
        type: "success",
        duration: 8000,
        action: undo
          ? {
              label: "Rückgängig",
              onClick: () => {
                void teacherApi
                  .undoSubjectRebind(undo)
                  .then(() => fetchTimetableEntries())
                  .catch((err) => {
                    setError(
                      extractErrorMessage(err, {
                        loadError: "Rückgängig fehlgeschlagen",
                      })
                    );
                  });
              },
            }
          : undefined,
      });
      const recategorize = opts.sourceCategory !== opts.targetCategory;
      setTimetableEntries((prev) =>
        prev.map((e) => {
          if (!e.id || !result.updated_ids.includes(e.id)) return e;
          const next = { ...e };
          if (opts.targetCategory === "subject") {
            next.subject =
              result.token ||
              next.subject_token ||
              next.subject ||
              result.target_name ||
              result.subject_name;
            next.subject_id = result.subject_id ?? result.target_id;
            next.subject_unclear = false;
          } else if (opts.targetCategory === "room") {
            next.room = result.target_name ?? next.room;
            next.room_id = result.target_id ?? next.room_id;
          } else {
            next.class_name = result.target_name ?? next.class_name;
            next.klass_id = result.target_id ?? next.klass_id;
          }
          if (recategorize && opts.sourceCategory === "subject") {
            next.subject = null;
            next.subject_id = null;
            next.subject_unclear = false;
          }
          if (recategorize && opts.sourceCategory === "room") {
            next.room = null;
            next.room_id = null;
          }
          if (recategorize && opts.sourceCategory === "klass") {
            next.class_name = null;
            next.klass_id = null;
          }
          return next;
        })
      );
      setRebind(null);
      void fetchTimetableEntries();
    } catch (err) {
      setRebindError(
        extractErrorMessage(err, {
          loadError: "Fach konnte nicht umgehängt werden",
          saveError: "Fach konnte nicht umgehängt werden",
        })
      );
    } finally {
      setRebindSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col px-3 pb-3">
      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {message}
          <button
            onClick={() => setMessage(null)}
            className="ml-2 text-sm underline"
          >
            Schliessen
          </button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-sm underline"
          >
            Schliessen
          </button>
        </div>
      )}

      <EffectivePlanView
        teacherId={selectedId}
        live={!isOwn}
        onPlan={(plan) => {
          setEffectivePlan(plan);
          setShowSaturday(Boolean(plan?.show_saturday));
        }}
        readOnlyLabel={
          !isOwn && selected
            ? `${selected.last_name}, ${selected.first_name} · nur lesen`
            : null
        }
        onWeek={setSelectedWeek}
        toolbarExtra={
          exportOptions?.can_export ? (
            <button
              type="button"
              className={`rounded border px-2 py-1 text-xs ${printOpen ? "border-blue-600 bg-blue-50 font-medium text-blue-700" : "border-gray-300 bg-white text-gray-700"}`}
              onClick={() => setPrintOpen((open) => !open)}
            >
              Drucken
            </button>
          ) : null
        }
        overlay={
          printOpen && exportOptions?.can_export ? (
            <TeacherPrintBar
              options={exportOptions}
              week={selectedWeek}
              subjectTeacherId={selectedId}
              onClose={() => setPrintOpen(false)}
            />
          ) : null
        }
        leading={
          <TeacherLetterDirectory
            title="Verzeichnis"
            compact
            items={visibleColleagues}
            query={query}
            onQueryChange={setQuery}
            selectedId={selectedId}
            onSelect={(t) => {
              setSelectedId(t.id);
              setRebind(null);
            }}
          />
        }
        single={
          <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            {!isOwn && selected ? (
              <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                <TeacherCode code={selected.teacher_code} />
                <span className="min-w-0 truncate font-semibold">
                  {selected.last_name}, {selected.first_name}
                </span>
                <span className="text-xs text-gray-400">nur lesen</span>
              </div>
            ) : null}
            <div className="min-h-0 flex-1">
              <EntriesTab<TeacherTimetableEntry>
                key={selectedId ?? "self"}
                entries={shownEntries}
                mode="dashboard"
                isLoadingEntries={displayLoading}
                readOnly={!isOwn || Boolean(selectedWeek)}
                fixedDays={
                  pdfGrid?.days?.length
                    ? pdfGrid.days
                    : showSaturday
                      ? ["MON", "TUE", "WED", "THU", "FRI", "SAT"]
                      : ["MON", "TUE", "WED", "THU", "FRI"]
                }
                pdfRows={pdfGrid ? gridRows : undefined}
                bellSlots={schoolTimeSlots}
                onUpdateEntry={isOwn ? handleUpdateTimetableEntry : undefined}
                onAddEntry={isOwn ? handleAddTimetableEntry : undefined}
                onSaveEntry={isOwn ? handleSaveTimetableEntry : undefined}
                onDeleteEntry={isOwn ? handleDeleteTimetableEntry : undefined}
                onRebindSubject={isOwn ? handleRebindSubject : undefined}
              />
            </div>
          </div>
        }
      />
      {rebind && (
        <SubjectRebindPanel
          preview={rebind.preview}
          anchor={rebind.anchor}
          initialSource={rebind.source}
          saving={rebindSaving}
          error={rebindError}
          onSave={(opts) => {
            void persistRebind(opts);
          }}
          onClose={() => {
            if (!rebindSaving) setRebind(null);
          }}
        />
      )}
    </div>
  );
}

function effectiveEntries(
  entries: TeacherTimetableEntry[],
  plan: EffectivePlan | null,
  week: WeekKey | null,
  axisKeys: string[],
  rows: Array<{ label: string; lesson?: number | null }>
): TeacherTimetableEntry[] {
  if (!plan || !week) return entries;
  const devs = plan.deviations.filter(
    (d) => sameWeek(week, d) && d.weekday !== "SUN"
  );
  const removed = new Set(
    devs.filter((d) => d.sign === "minus" && d.entry_id).map((d) => d.entry_id)
  );
  const kept = entries.filter((entry) => !removed.has(entry.id));
  const added = devs
    .filter((d) => d.sign === "plus")
    .map((d) => {
      const clock = `${d.start.slice(0, 5).padStart(5, "0")}–${(d.end || d.start).slice(0, 5).padStart(5, "0")}`;
      const hit = rows.findIndex((row) => row.label === clock);
      return {
        id: d.lesson_id,
        teacher_code: null,
        day_of_week: d.weekday,
        time_slot_label: hit >= 0 ? (axisKeys[hit] ?? clock) : clock,
        lesson_number: hit >= 0 ? (rows[hit]?.lesson ?? null) : null,
        subject: d.subject || d.other_name || d.label,
        room: d.room,
        class_name: d.class_name,
        is_multi_period: false,
        lesson_count: 1,
        multi_period_position: null,
        raw_content: "",
        is_active: true,
        is_manual_entry: false,
        is_cancelled: false,
        cancelled_reason: null,
        date: d.date,
        week_offset: 0,
        version: 0,
      };
    });
  return [...kept, ...added];
}
